import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useRouter } from "expo-router";
import type {
  TextStyle,
  ViewStyle,
} from "react-native/Libraries/StyleSheet/StyleSheetTypes";

import ExpandableOptions from "../ExpandableOptions/ExpandableOptions";

import EmailInput from "./Components/EmailInput";
import PasswordInput from "./Components/PasswordInput";
import RememberLogin from "./Components/RememberLogin";

import { useSession } from "@/context/auth/useSession";
import { ApiError } from "@/schemas/api/error";
import { type AppUserSession } from "@/schemas/auth/session";
import { isValidEmailAddress } from "@/schemas/forms/email";
import { isValidPassword } from "@/schemas/forms/password";
import AuthService from "@/services/internal/auth";
import {
  alertUnexpectedApiError,
  showUnconfirmedAccountAlert,
} from "@/utils/alerts";
import type { LoginCredentials } from "@/schemas/auth/login";
import {
  clearLoginCredentialsFromStorage,
  getLoginCredentialsFromStorage,
  setLoginCredentialsInStorage,
} from "@/hooks/authentication";
import { AuthUserNotConfirmedError } from "@/schemas/auth/errors";

function LoginForm(): React.JSX.Element {
  const router = useRouter();
  const { signIn } = useSession();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(true);

  const isButtonDisabled = useMemo(() => {
    return (
      loading ||
      !isValidEmailAddress(email).isValid ||
      !isValidPassword(password).isValid
    );
  }, [email, password, loading]);

  const fetchCreds = async (): Promise<void> => {
    const creds = await getLoginCredentialsFromStorage();
    if (creds) {
      setEmail(creds.email);
      setPassword(creds.password);
    }
  };

  // Apply saved credentials when they're loaded
  useEffect(() => {
    fetchCreds();
  }, []);

  const validateInputs = (): boolean => {
    if (email && password) {
      const emailValidation = isValidEmailAddress(email);
      const passwordValidation = isValidPassword(password);

      if (!emailValidation.isValid) {
        Alert.alert("Invalid Email Address", emailValidation.message, [
          { text: "OK" },
        ]);
        return false;
      }
      if (passwordValidation.isValid === false) {
        Alert.alert("Invalid Password", passwordValidation.message, [
          { text: "OK" },
        ]);
        return false;
      }
    } else {
      if (!email) {
        Alert.alert("Missing Email", "Please enter your email.", [
          { text: "OK" },
        ]);
        return false;
      }
      if (!password) {
        Alert.alert("Missing Password", "Please enter your password.", [
          { text: "OK" },
        ]);
      }
    }
    return true;
  };

  const onSubmit = async (): Promise<void> => {
    const isValid = validateInputs();
    if (isValid === false) {
      return;
    }

    setLoading(true);
    const authService = new AuthService();

    try {
      if (rememberMe) {
        const creds: LoginCredentials = {
          email: email,
          username: "",
          password,
        };
        await setLoginCredentialsInStorage(creds);
      } else {
        await clearLoginCredentialsFromStorage();
      }

      const session: AppUserSession = await authService.login({
        email: email,
        password: password,
      });
      await signIn(session);
      router.replace("/(main)/explore/home");
    } catch (error) {
      const err = error as ApiError;
      switch (err.message) {
        case AuthUserNotConfirmedError.message: {
          showUnconfirmedAccountAlert(router);
          break;
        }
        default:
          // todo: this is wrong; we shouldn't pass ApiError but instead the error.
          alertUnexpectedApiError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Get the appropriate button style based on disabled state
  const getButtonStyle = (): ViewStyle | ViewStyle[] => {
    return isButtonDisabled
      ? [styles.signInButton, styles.signInButtonDisabled]
      : styles.signInButton;
  };

  // Get the appropriate text style based on disabled state
  const getButtonTextStyle = (): TextStyle | TextStyle[] => {
    return isButtonDisabled
      ? [styles.signInButtonText, styles.signInButtonTextDisabled]
      : styles.signInButtonText;
  };

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.formContainer}
      >
        <View style={styles.card}>
          <Text style={styles.title}>Sign in</Text>
          <Text style={styles.label}>Email</Text>
          <EmailInput
            value={email}
            onChangeText={setEmail}
            placeholder="Enter your email"
          />
          <Text style={styles.label}>Password</Text>
          <PasswordInput
            value={password}
            onChangeText={setPassword}
            placeholder="Enter your password"
          />

          <RememberLogin
            rememberMe={rememberMe}
            setRememberMe={setRememberMe}
          />

          {loading ? (
            <ActivityIndicator size="large" />
          ) : (
            <TouchableOpacity
              style={getButtonStyle()}
              onPress={onSubmit}
              disabled={isButtonDisabled} // Properly use the disabled state
              testID="sign-in-button"
            >
              <Text testID="sign-in-button" style={getButtonTextStyle()}>
                Sign In
              </Text>
            </TouchableOpacity>
          )}
          <ExpandableOptions />
        </View>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  formContainer: {
    width: "100%",
    maxWidth: 400,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 10,
    textAlign: "center",
    color: "#4A5568",
  },
  card: {
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderRadius: 10,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 5,
    color: "#4A5568",
  },
  signInButton: {
    backgroundColor: "#4299E1",
    borderRadius: 5,
    padding: 15,
    alignItems: "center",
    marginTop: 10,
  },
  signInButtonDisabled: {
    backgroundColor: "#A0AEC0", // Lighter blue/gray for disabled state
    opacity: 0.7,
  },
  signInButtonText: {
    color: "white",
    fontSize: 18,
    fontWeight: "600",
  },
  signInButtonTextDisabled: {
    color: "rgba(255, 255, 255, 0.8)", // Slightly transparent white text
  },
});

export default LoginForm;
